import { Link } from 'react-router-dom';
import { STANDARD_ALCOHOL_ML, ML_PER_US_FL_OZ } from '@/lib/metrics';
import { brand } from '@/config/brand';

export function MethodologyPage() {
  return (
    <article className="panel prose-page">
      <h1>Methodology</h1>
      <p>
        {brand.name} compares drinks by alcohol content relative to calories and
        price. Ratios describe composition and value — not health grades, taste,
        or predicted intoxication. There is no BAC estimator and no single
        “best drink” score that mixes unrelated goals.
      </p>

      <h2>Units</h2>
      <ul>
        <li>Volume is stored as milliliters.</li>
        <li>ABV is percentage points (4.2 means 4.2% alcohol by volume).</li>
        <li>1 US fl oz = {ML_PER_US_FL_OZ} mL (exact).</li>
        <li>
          STANDARD_ALCOHOL_ML = 0.6 × {ML_PER_US_FL_OZ} = {STANDARD_ALCOHOL_ML}{' '}
          mL ≈ 14 g ethanol. We use one volume constant for “standard drink”
          displays and do not mix gram and volume constants across screens.
        </li>
      </ul>
      <p>
        Pure alcohol in a serving (mL) = serving mL × (ABV% ÷ 100). Package pure
        alcohol multiplies that idea across container count and container size.
      </p>

      <h2>Primary ratios</h2>
      <ul>
        <li>
          <strong>Alcohol (mL) / 100 cal</strong> — milliliters of pure alcohol
          per 100 food calories. Toggle to fl oz when preferred. Higher means
          fewer calories for equivalent alcohol. Rescaling the labeled serving
          size does not change this ratio when ABV and calories scale together.
        </li>
        <li>
          <strong>Alcohol (mL) / $</strong> — milliliters of pure alcohol per
          dollar of the selected package. Higher means more alcohol per dollar.
          Based on package volume and package price, not a single pour size.
        </li>
      </ul>
      <p>
        We do <strong>not</strong> multiply those into a single “alcohol / cal /
        $” score: dividing calorie efficiency by serving price changes when the
        labeled serving size changes, even if the drink and package do not.
        Instead, compare the two ratios separately, or set a baseline and look
        for drinks that are better on both calories and cost per standard drink
        (lower calories and dollars per standard drink are better for equivalent
        alcohol).
      </p>

      <h2>Worked example</h2>
      <p>
        Suppose Drink A is a 12 fl oz light lager at 4.0% ABV with 95 calories,
        and Drink B is a 1.5 fl oz spirits pour at 40% ABV with 97 calories
        (illustrative). Drink A contains about 14.2 mL pure alcohol; Drink B
        about 17.7 mL. Alcohol per 100 cal is higher for the spirit pour in this
        sketch because most of its energy is alcohol, while the lager also
        carries non-alcohol calories. Package alcohol-per-dollar still depends
        on what you pay for a six-pack versus a bottle — which is why price and
        calorie rankings are shown side by side, not mashed into one number.
      </p>
      <p>
        For narrative walkthroughs with live catalog drinks, see{' '}
        <Link to="/guides/how-to-read-boozeratio-rankings/">
          How to read BoozeRatio rankings
        </Link>{' '}
        and{' '}
        <Link to="/guides/light-beer-vs-double-ipa/">
          Light beer vs double IPA
        </Link>
        .
      </p>

      <h2>Prices</h2>
      <p>
        Precedence: your override → selected reference offer → regular reference
        → labeled estimate → unavailable. Virginia ABC prices, when imported, are
        regional shelf references for that control state — never a national
        average. Utah DABS and other retail snapshots similarly describe a place
        and time. Retail grocery beer prices and on-premise bar prices are not
        mixed in one unlabeled ranking.
      </p>
      <p>
        Beer, seltzer, and cider are generally not sold on Virginia ABC price
        lists; those categories rely on other reference offers or estimates until
        you enter a local price. Read{' '}
        <Link to="/guides/how-regional-abc-prices-are-used/">
          How regional ABC prices are used
        </Link>{' '}
        for more detail.
      </p>

      <h2>Data quality</h2>
      <p>
        Unknown is null, never zero. Missing-price drinks can still rank on
        calories; missing-calorie drinks can still rank on price. Nutrition
        status is labeled verified, estimated, or unknown so you can filter.
        Carbs are optional filters — total calories already include carbohydrate
        energy when calories are present.
      </p>
      <p>
        Recipe-based mixed drinks use explicit ingredient models for home
        preparation cost and nutrition estimates. They are not bar menu prices.
      </p>

      <h2>Responsible use</h2>
      <p>
        {brand.adultNotice} Use rankings to understand labels and value — not to
        maximize intake. Feedback and corrections:{' '}
        <Link to="/feedback/">feedback form</Link>.
      </p>
    </article>
  );
}
